import * as a from '@mitranim/js/all.mjs'
import * as dg from '@mitranim/js/dom_global'
import * as dr from '@mitranim/js/dom_reg.mjs'
import * as c from './conf.mjs'
import * as u from './util.mjs'

export function MixFormElem(val) {return MixFormElemCache.goc(val)}

class MixFormElemCache extends a.DedupMixinCache {
  static make(cls) {
    return class MixFormElemCls extends cls {
      // Extends the native `.form` getter. Works even before the child is
      // attached to the parent. TODO consider moving this to `MixElem`.
      get form() {return super.form ?? this.ancForm()}
      getForm() {return super.form ?? this.ancForm()}
      ancForm() {return this.anc(dg.global.HTMLFormElement)}

      // Method `.exec` is implemented by our form class.
      // Method `.submit` is standard.
      formSubmit() {
        const form = this.getForm()
        return form?.exec?.() ?? form.submit()
      }
    }
  }
}

// TODO move to a config file.
const canExecCommand = c.DOM_AVAILABLE && a.isFun(document.execCommand)

/*
The "legacy" API `document.execCommand` adds an "undo" entry,
while the "modern" API `.setRangeText` does not.
TODO move to `util-dom.mjs` and use for `resetInputValue`.
*/
function docInserted(src) {
  src = a.laxStr(src)
  return canExecCommand && document.execCommand(`insertText`, false, src)
}

/*
Mixin for functionality shared between `HTMLInputElement` and
`HTMLTextAreaElement`.
*/
function MixInput(cls) {
  return class MixInputCls extends cls {
    replaceAll(src) {
      src = a.laxStr(src)
      if (canExecCommand) {
        this.focusOpt()
        u.selectElementContent(this)
        if (docInserted(src)) return
      }
      this.value = src
    }

    /*
    Required for `document.execCommand` in cases when another element is
    focused, for example on button click. Not required for `.setRangeText`.
    */
    focusOpt() {if (document.activeElement !== this) this.focus()}

    clearSel() {this.replaceSel()}

    /*
    Replaces the current selection with the given text, adjusting the selection
    range to match the new text. Does NOT automatically change the current
    selection.

    Note: to select the entire content, call `u.selectElementContent`.
    */
    replaceSel(src) {
      src = a.laxStr(src)

      if (canExecCommand) {
        this.focusOpt()
        if (docInserted(src)) {
          const start = this.selectionStart
          this.setSelectionRange(start, start + src.length)
          return
        }
      }

      this.setRangeText(src)
    }

    // TODO use `docInserted` if possible, for an "undo" entry.
    insert(src) {
      this.setRangeText(a.laxStr(src), this.selectionEnd, this.selectionEnd, `end`)
    }

    // Invoked by `Form` when building form body for JSON.
    addFormValue(tar) {
      a.reqDict(tar)
      const name = this.name
      if (!a.optStr(name)) return
      tar[name] = a.optStr(this.value) ?? null
    }
  }
}

export class Input extends MixFormElem(MixInput(u.MixElem(dg.global.HTMLInputElement))) {
  static {dr.reg(this)}

  foc() {return `visibleAutoFocus` in this.dataset}
  connectedCallback() {if (this.foc()) u.visObs?.observe(this)}
  disconnectedCallback() {u.visObs?.unobserve(this)}
  onVisChange(visible) {if (visible) this.focus()}
  setValue(val) {return this.replaceAll(a.renderLax(val)), this}
  isEmpty() {return !this.value}
  clear() {return this.replaceAll(), this}

  replaceAll(src) {
    src = a.laxStr(src)
    if (this.supportsSelection()) super.replaceAll(src)
    else this.value = src
  }

  /*
  Some input types, such as `<input type=time>`, explicitly reject selection.
  Attempting to modify selection with `.setSelectionRange` causes exceptions.

  Incomplete. Extend as needed.
  */
  supportsSelection() {return this.type !== `time`}

  // Used by numeric inputs.
  getValueAsNumber() {
    return a.onlyFin(this.valueAsNumber) ?? u.parseNum(this.value)
  }
}

export class Textarea extends MixFormElem(MixInput(u.MixElem(dg.global.HTMLTextAreaElement))) {
  static {dr.reg(this)}

  /*
  Ideally, here we would auto-resize to fit the content. Unfortunately, it may
  cause OTHER unrelated elements to briefly change dimensions and revert back.
  Without CSS transitions it might not be noticeable, but with CSS transitions
  it looks very jarring. At the time of writing, the problem occurs in Chrome.
  */
  connectedCallback() {
    this.oninput = this.onInput
    if (`autofocus` in this.dataset) this.focus()
  }

  onInput() {this.resize()}
  resize() {return u.resizeTextarea(this)}
  hasSel() {return this.selectionStart !== this.selectionEnd}

  sel() {
    const start = this.selectionStart
    const end = this.selectionEnd
    return end >= start ? this.value.slice(start, end) : ``
  }

  cursorPre() {
    const ind = this.selectionStart
    return ind >= 0 ? this.value.slice(0, ind) : ``
  }

  cursorSuf() {
    const ind = this.selectionEnd
    return ind >= 0 ? this.value.slice(ind) : ``
  }

  replaceSel(src) {
    super.replaceSel(src)
    return this.resize()
  }

  /*
  If the current selection is wrapped with the given prefix-suffix on both
  sides, removes the prefix-suffix from around the selection. Otherwise wraps
  the current selection in the prefix-suffix. Matches Discord behavior.
  */
  toggleWrapSel(str) {
    if (!a.reqStr(str)) return this

    const start = this.selectionStart
    const end = this.selectionEnd
    const src = this.value
    const sel = src.slice(start, end)

    if (
      start >= 0 && src.slice(0, start).endsWith(str) &&
      end >= start && src.slice(end).startsWith(str)
    ) {
      this.setSelectionRange(start - str.length, end + str.length)
      this.replaceSel(sel)
      this.setSelectionRange(start - str.length, end - str.length)
    }
    else {
      this.replaceSel(a.str(str, sel, str))
      this.setSelectionRange(start + str.length, end + str.length)
    }
    return this
  }

  trim() {this.replaceAll(a.trim(this.value))}

  maybeSubmitForm(eve) {
    if (!u.isEventKeydownSubmit(eve)) return
    const form = this.anc(HTMLFormElement)
    if (!form?.onsubmit) return
    a.eventKill(eve)
    form.onsubmit(eve)
  }
}

export class LangSelect extends u.MixElem(dg.global.HTMLSelectElement) {
  static {dr.reg(this)}

  connectedCallback() {this.onchange = this.onChange}
  onChange() {u.switchLang(this.selectedOptions[0]?.value)}
}

/*
Can be used on some native inputs, such as `<option>`, to support storing
structured values (like JSON dicts) in string-encoded form. Should be used
only on native DOM classes with their own built-in support for the `.value`
getter-setter (which is always a string).

The encoded value can be retrieved and used in two ways. One scenario is
generating a form-encoded POST body or URL query. Then we use the native
`.value`, which is either a text encoding (for scalar values) or a JSON
encoding (for non-scalar values such as dicts and lists). The other scenario is
generating a structured value for JSON. Then we decode the value from the
dataset, which is always a JSON encoding (when present). Note the difference
between text encoding and JSON encoding. A JSON encoding is not always
generated from a text-encoded value. See the comment on scalars below. Also
compare the Go interfaces `encoding.TextMarshaler` and `json.Marshaler`.

TODO use `.replaceAll` instead of `.value =` when the method is available.
It adds an "undo" entry when possible.
*/
export function MixElemValued(cls) {
  return class MixElemValuedCls extends cls {
    getValue() {
      const src = this.dataset.value
      if (!src) return this.value
      if (src === `null`) return undefined
      return a.jsonDecode(src)
    }

    setValue(val) {
      if (a.isNil(val)) {
        this.dataset.value = `null`
        this.value = ``
        return this
      }

      if (a.isStr(val)) {
        delete this.dataset.value
        this.value = val
        return this
      }

      /*
      Caution. For booleans and numbers, text encoding and JSON encoding are the
      same. However, for various other scalar types, text encoding and JSON
      encoding are NOT the same, typically because JSON encoding involves
      additional quotes.
      */
      if (a.isScalar(val)) {
        this.dataset.value = a.jsonEncode(val)
        this.value = a.render(val)
        return this
      }

      val = a.jsonEncode(val)
      this.dataset.value = val
      this.value = val
      return this
    }
  }
}
